import { Alert, Button } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { formatUsPhone } from "../../../components/PhoneInputs/PhoneInput.tsx"
import { RoutesPaths } from "../../../constants/routes.ts"
import { VerifyPhoneModal } from "./VerifyPhoneModal.tsx"
import { type SmsVerificationState } from "./sms-verification.services.ts"
import { type SmsLock } from "./use-sms-verification.ts"

export function SmsStoppedBanner({
  startNumber
}: {
  startNumber?: string | null
}) {
  const { t } = useTranslation()
  return (
    <Alert
      type="warning"
      showIcon
      message={
        startNumber
          ? t("smsVerification.stopped", { number: formatUsPhone(startNumber) })
          : t("smsVerification.stoppedNoNumber")
      }
    />
  )
}

interface SmsAlertsNoticeProps {
  state: SmsVerificationState | null
  lock: SmsLock
  failed?: boolean
  onRetry?: () => void
  onVerified: (state: SmsVerificationState) => void
}

export function SmsStatusUnreadable({ onRetry }: { onRetry?: () => void }) {
  const { t } = useTranslation()
  return (
    <Alert
      type="warning"
      showIcon
      message={t("smsVerification.unreadable")}
      action={
        onRetry && (
          <Button size="small" onClick={onRetry}>
            {t("smsVerification.retry")}
          </Button>
        )
      }
    />
  )
}

/** Why the SMS switches below are locked, and the way to unlock them. */
export function SmsAlertsNotice({
  state,
  lock,
  failed = false,
  onRetry,
  onVerified
}: SmsAlertsNoticeProps) {
  const { t } = useTranslation()
  const [verifying, setVerifying] = useState(false)

  if (!state && failed) return <SmsStatusUnreadable onRetry={onRetry} />
  if (!state?.available || !lock) return null
  if (lock === "stopped")
    return <SmsStoppedBanner startNumber={state.start_number} />

  return (
    <>
      <Alert
        type="info"
        showIcon
        message={t("smsVerification.prompt")}
        action={
          state.phone ? (
            <Button
              size="small"
              type="primary"
              onClick={() => setVerifying(true)}
            >
              {t("smsVerification.verifyButton")}
            </Button>
          ) : (
            <Link to={RoutesPaths.settings.profile}>
              {t("smsVerification.addPhone")}
            </Link>
          )
        }
      />
      {state.phone && (
        <VerifyPhoneModal
          open={verifying}
          phone={state.phone}
          startNumber={state.start_number}
          onClose={() => setVerifying(false)}
          onVerified={(verified) => {
            setVerifying(false)
            onVerified(verified)
          }}
        />
      )}
    </>
  )
}
